import type {ReactNode} from 'react';
import {cx} from './cx';

/**
 * One choice in an option group: a required single-select (spice) or an optional
 * multi-select (extras).
 *
 * # A real input inside a label, not a button with `aria-checked`
 *
 * The native control brings keyboard behaviour, group semantics and correct
 * announcement for free, and the whole 48px `<label>` is the tap target rather
 * than the 20px mark. The mark itself is `appearance: none` and drawn in CSS —
 * a 20px circle or square, ink when checked for a checkbox and Chili Red for a
 * radio.
 *
 * That colour split is deliberate and worth keeping: a **chosen extra** must not
 * compete with the screen's one solid red action, so it is ink; a **required
 * choice** is part of the path to that action, so it is red.
 *
 * # The cap has to say something
 *
 * The design caps extras at three and enforces it by silently ignoring the
 * fourth tap — no disabled styling, no counter, no message. A control that
 * refuses a tap and says nothing is indistinguishable from a broken one, so this
 * component takes `disabled` **and** `disabledReason`: unchosen rows go grey at
 * the cap and the reason is announced with the row. Already-chosen rows stay
 * live, or the guest cannot change their mind.
 *
 * ```tsx
 * <OptionRow
 *   type="checkbox" name="extras" value={o.id}
 *   checked={chosen.has(o.id)}
 *   disabled={atCap && !chosen.has(o.id)}
 *   disabledReason={t('q4.extrasCap', {max: 3})}
 *   onChange={toggle}
 *   label={ln(o.name)}
 *   note={<MoneyDelta satang={o.price_delta_satang} />}
 * />
 * ```
 */
export function OptionRow({
  type,
  name,
  value,
  checked,
  onChange,
  label,
  note,
  disabled = false,
  disabledReason,
  className,
}: {
  type: 'radio' | 'checkbox';
  /** Same `name` across a radio group — that is what makes it one group. */
  name: string;
  value: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** Localised. The visible, announced name of the choice. */
  label: ReactNode;
  /** Right-hand column: a `MoneyDelta`, usually. Never the only meaning. */
  note?: ReactNode;
  disabled?: boolean;
  /** Localised, and required with `disabled` — say why the tap did nothing. */
  disabledReason?: string;
  className?: string;
}) {
  return (
    <label className={cx('pk-option', className)} data-disabled={disabled || undefined}>
      {/*
        `aria-disabled`, not the `disabled` attribute. A natively disabled input
        is skipped by the tab order and by most screen-reader row navigation, so
        the guest who most needs to hear *why* the cap stopped them is the one
        who never reaches the sentence. Left focusable and announced as disabled,
        with the change suppressed here — a controlled input whose state does not
        move cannot be checked, whatever the browser does with the event.
      */}
      <input
        className="pk-option-mark"
        type={type}
        name={name}
        value={value}
        checked={checked}
        aria-disabled={disabled || undefined}
        onChange={(event) => {
          if (disabled) return;
          onChange(event.target.checked);
        }}
      />
      <span className="pk-option-label">{label}</span>
      {note ? <span className="pk-option-note">{note}</span> : null}
      {/*
        Inside the <label>, so it joins the control's accessible name rather than
        floating beside it as unattached text.
      */}
      {disabled && disabledReason ? (
        <span className="pokpok-sr-only">{disabledReason}</span>
      ) : null}
    </label>
  );
}

/**
 * The list a group of `OptionRow`s sits in, and the helper line above it.
 *
 * `helper` is where "choose one" and "up to three" go — at body size in the hint
 * colour, because the design's 11.5px is below this surface's floor and the
 * hierarchy has to come from colour instead.
 */
export function OptionGroup({
  children,
  helper,
  className,
}: {
  children: ReactNode;
  helper?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cx(className)}>
      {helper ? <p className="pk-hint">{helper}</p> : null}
      {children}
    </div>
  );
}
